import {
	useIsFreeMembership,
	useUserProfile,
	useUserSubscription,
} from "@bodhistreams/api/src";
import { BodhiLoader, bodhiMessage } from "@bodhistreams/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CertificateForm from "../components/certificate/CertificateForm";
import CertificateGate from "../components/certificate/CertificateGate";
import CertificateHeader from "../components/certificate/CertificateHeader";
import CertificatePreview from "../components/certificate/CertificatePreview";
import "../components/certificate/certificateLayout.css";
import { downloadCertificatePdf } from "../components/certificate/certificatePdf";
import {
	buildInitialValues,
	saveValues,
	validateValues,
} from "../components/certificate/certificateUtils";
import PageLayout from "./PageLayout";

const CertificatePage = () => {
	const navigate = useNavigate();
	const {
		data: user,
		isLoading: isUserLoading,
		refetch: refetchUserProfile,
	} = useUserProfile();
	const { data: userSubscription, isLoading: isSubscriptionLoading } =
		useUserSubscription(Boolean(user));
	const isFreeMembership = useIsFreeMembership();

	const [values, setValues] = useState(() => buildInitialValues(null));
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!user) {
			refetchUserProfile();
			return;
		}

		setValues(buildInitialValues(user));
	}, [refetchUserProfile, user]);

	const isLoading = isUserLoading || isSubscriptionLoading;
	const membershipName =
		userSubscription?.membership_name ||
		user?.membership_name ||
		"Paid Membership";
	const hasPaidAccess = !isFreeMembership && Boolean(membershipName);

	const commitField = (field, value) => {
		const next = { ...values, [field]: value };
		setValues(next);
		saveValues(next);
		if (errors[field]) {
			setErrors((prev) => {
				const { [field]: _removed, ...rest } = prev;
				return rest;
			});
		}
	};

	const setField = (field) => (event) => commitField(field, event.target.value);

	const handleDownload = async () => {
		const nextErrors = validateValues(values);
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			bodhiMessage.error("Please complete the required fields");
			return;
		}
		saveValues(values);
		try {
			await downloadCertificatePdf({ values, membershipName });
			bodhiMessage.success("Certificate downloaded");
		} catch (_err) {
			bodhiMessage.error("Failed to generate certificate");
		}
	};

	if (isLoading) {
		return (
			<PageLayout>
				<div className="cert-loading">
					<BodhiLoader mode="inline" size={64} backgroundOpacity={0} />
				</div>
			</PageLayout>
		);
	}

	if (!hasPaidAccess) {
		return (
			<PageLayout
				fullWidthContent={
					<CertificateGate
						onBack={() => navigate("/profile")}
						onViewPlans={() => navigate("/pricing")}
					/>
				}
			/>
		);
	}

	return (
		<PageLayout
			fullWidthContent={
				<div className="cert-page">
					<div className="cert-inner">
						<CertificateHeader
							onBack={() => navigate("/profile")}
							onDownload={handleDownload}
						/>
						<div className="cert-stack">
							<CertificateForm
								values={values}
								errors={errors}
								setField={setField}
							/>
							<CertificatePreview
								values={values}
								membershipName={membershipName}
								user={user}
							/>
						</div>
					</div>
				</div>
			}
		/>
	);
};

export default CertificatePage;
